import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Timer, ArrowRight } from 'lucide-react';

import { useGame } from '../../context/GameContext';

/**
 * Persistent floating pill that appears whenever a game is in progress and
 * the user has navigated away from the GameScreen (e.g. browsed Home or
 * Settings). Shows the round + countdown so the user always knows what's
 * happening, and a tap returns them to the game. Hides when the user is on
 * the actual game route.
 */
export function ActiveGamePill() {
  const { state } = useGame();
  const location = useLocation();
  const navigate = useNavigate();
  const [now, setNow] = useState(Date.now());

  // Force a 1-second tick so the displayed timer keeps moving even when
  // the user is on a screen that doesn't already re-render every second.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const inGame = state.currentScreen === 'game' || state.isGameActive;
  const onGameRoute = location.pathname.startsWith('/game') || location.pathname.startsWith('/results') || location.pathname.startsWith('/game-results') || location.pathname.startsWith('/room/');
  if (!inGame || onGameRoute) return null;
  if (!state.roomCode) return null;

  const seconds = Math.max(0, state.timeRemaining || 0);
  const urgent = seconds > 0 && seconds <= 10;
  const minutes = Math.floor(seconds / 60);
  const sec = seconds % 60;
  const timeStr = `${minutes}:${sec.toString().padStart(2, '0')}`;

  return (
    <button
      type="button"
      onClick={() => navigate(`/game/${state.roomCode}`)}
      style={{
        position: 'fixed',
        top: 'max(12px, env(safe-area-inset-top))',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 100,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 14px 8px 10px',
        borderRadius: 999,
        background: urgent ? 'var(--accent)' : 'var(--surface)',
        color: urgent ? 'white' : 'var(--ink)',
        border: '1px solid var(--line)',
        boxShadow: '0 8px 24px -8px rgba(22,22,26,0.20), 0 0 0 1px var(--line)',
        cursor: 'pointer',
        fontFamily: 'var(--ui)',
        fontWeight: 600,
        fontSize: 13,
        animation: urgent ? 'wr-pill-flash 1s ease-in-out infinite' : undefined,
        // hint that we're suppressed during print
      }}
      aria-label="Return to game"
    >
      <Timer size={14} color={urgent ? 'white' : undefined} />
      <span style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
        {timeStr}
      </span>
      <span style={{ opacity: urgent ? 0.95 : 0.7 }}>· R{state.currentRound || '?'}</span>
      <ArrowRight size={14} />
    </button>
  );
}

/* Inject the flash keyframe once (used while urgent). */
const styleId = 'wr-pill-flash-style';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const s = document.createElement('style');
  s.id = styleId;
  s.textContent = `
    @keyframes wr-pill-flash {
      0%, 100% { box-shadow: 0 8px 24px -4px rgba(255,90,31,0.5), 0 0 0 1px var(--line); }
      50% { box-shadow: 0 8px 24px -4px rgba(255,90,31,0.9), 0 0 0 4px rgba(255,90,31,0.18); }
    }
  `;
  document.head.appendChild(s);
}
